/**
 * Documentation Prose Content Schema
 *
 * The block vocabulary for the customer documentation site (and the internal
 * runbooks that share its content model). Platform-global, NOT per-school —
 * this is the prose quadrant, governed by `docs/platform/prose-authoring-contract.md`.
 *
 * `DocsContentSchema` is the SECURITY BOUNDARY for author-supplied block JSON
 * (Rule P1), not a convenience validator. It mirrors `blog-post.ts` deliberately
 * (KTD1): same closed allowlist, same flat-primitive props, same https-only URL
 * rule, same non-destructive parse. Read that file first — the two are meant to
 * stay recognisably the same shape.
 *
 *   - Block `type` is ALLOWLISTED (`DOCS_BLOCK_TYPES`). An unknown type is
 *     rejected, never stripped — silently dropping blocks loses an author's
 *     work, and silently keeping them would let a client invent a block the
 *     renderer treats specially.
 *   - Props are FLAT primitives (string / number / boolean). Nested objects and
 *     arrays are rejected, so no unvalidated sub-tree rides through a prop bag.
 *   - Every URL-bearing prop must parse as an absolute `https:` URL. Rejected,
 *     never coerced. An inline link `href` answers to a SEPARATE, slightly wider
 *     boundary — see `isDocsLinkHref` — because documentation is mostly internal
 *     cross-references. The two are not the same rule; do not unify them.
 *   - Block CONTENT has exactly two arms, and each block type takes exactly one:
 *     an inline-content array, or — for `table` alone — the rows-and-cells
 *     OBJECT BlockNote calls `tableContent` (KTD7). A table cell is bounded to
 *     inline content, so the second nesting axis a table introduces is closed at
 *     depth one. See `DocsTableCellSchema`.
 *
 * CONTAINERS NEVER NEST INSIDE CONTAINERS (R8 / Rule P2). Multi-part constructs
 * are container blocks with children rather than structured props (KD10), and
 * the bound is enforced here rather than documented and hoped for:
 *
 *   - EVERY block type declares the closed set of child types it admits — see
 *     `DOCS_BLOCK_CHILD_TYPES`. Nothing outside that set is accepted, and a type
 *     that declares no set at all is a compile error.
 *   - `callout`, `stepList`, and `faq` are ROOT-ONLY: a container inside another
 *     container, or inside a list item, is rejected. That is the invariant — not
 *     a depth count — and it is what stops documentation acquiring the arbitrary
 *     block-in-block structure an MDX corpus grows.
 *   - `step` and `faqItem` are CHILD-ONLY: they are the items their construct is
 *     made of, and a document with one at the root is rejected.
 *   - The list-item cycle is the one place depth needs its own bound: a nested
 *     list item carries no children, so list nesting stops at one level.
 *
 * COLUMNS ARE THE ONE CONTAINER THAT HOLDS ANOTHER STRUCTURE, and the invariant
 * is restated, not abandoned (SCH-1470 plan KTD2, bound by SCH-2162):
 *
 *   - `columnList` is ROOT-ONLY and holds only `column`s, at least two of them.
 *   - `column` is CHILD-ONLY: it exists only inside a `columnList`, and it
 *     admits the same leaf set as a callout — no container, and no `columnList`.
 *   - So column grids cannot nest, and no container ever sits inside a column.
 *     Maximum depth is `columnList > column > leaf`.
 *
 *   Why `column` does not admit containers: widening an allowlist later is safe,
 *   narrowing it is not. There is no corpus evidence for a callout inside a
 *   column; if one appears, adding it to the column's set invalidates no stored
 *   page, whereas shipping it now and removing it later would make every page
 *   that used it unsaveable.
 *
 *   A column's `width` must pass the shared bounds in `./column-width` (KTD9).
 *
 * This vocabulary is CORPUS-DRIVEN, not theoretical. Containers admit a leaf set
 * (`DOCS_CONTAINER_LEAF_TYPES`) rather than a single child type because the
 * documentation site already needs it: a warning callout holding a bulleted list
 * (`troubleshooting/getting-help.mdx`), steps holding several paragraphs plus a
 * list plus a blockquote (`start.mdx`, and four other files), FAQ answers holding
 * both list kinds (`troubleshooting/common-issues.mdx`).
 *
 * ONE KNOWN LOSSY CASE for the migration (U12): `getting-started/quick-start.mdx`
 * has a `<Callout>` INSIDE a `<Step>`. A callout is root-only, so it flattens to
 * a sibling of the step list. That is accepted, and it is the only container-in-
 * container instance in the corpus.
 *
 * KEEPING THE EDITOR IN SYNC: the docs editor must build its BlockNote schema
 * from exactly this vocabulary, by REMOVING `DOCS_EXCLUDED_DEFAULT_BLOCK_TYPES`
 * from BlockNote's defaults rather than re-listing the allowed ones, so an
 * upstream rename breaks the editor's `satisfies` at compile time instead of a
 * block quietly reappearing. A block the editor can insert but this allowlist
 * omits fails Zod at the API boundary, which reads to the author as a LOST PAGE.
 *
 * See docs/plans/2026-08-08-002-feat-content-authoring-strategy-plan.md (U2).
 */

import { z } from 'zod'

// The https predicate is shared with the blog rather than re-implemented: both
// are prose-quadrant surfaces answering to the same Rule P1 constraint, and a
// security primitive with two copies is a security primitive with one stale copy.
import { isHttpsUrl } from './blog-post'
import { COLUMN_WIDTH_MESSAGE, isColumnWidth } from './column-width'

// ============================================
// Link href — a SEPARATE, wider boundary than URL props
// ============================================

/**
 * TWO boundaries, deliberately not unified — do not collapse them.
 *
 *   - URL-bearing PROPS (image `url`, video `url`, anything ending in `Url`) stay
 *     absolute-https-only, via `isHttpsUrl`. A media source is FETCHED, and the
 *     corpus has zero relative media sources, so nothing is gained by relaxing it.
 *   - Inline link `href` additionally permits a ROOT-RELATIVE path, because a
 *     documentation site is mostly internal cross-references: 11 of the corpus's
 *     20 links and all 33 `LinkCard` targets are `/docs/...` paths.
 *
 * `mailto:` is excluded on purpose (user-directed) — the boundary stays at two
 * forms, and the corpus's one `support@` link is a migration item, not a schema
 * concession. Doc-relative paths (`./asset-uploads`) are excluded too: they are a
 * repository artefact with no meaning once the database is the source of truth,
 * so migration rewrites them to absolute site paths.
 *
 * The `//` exclusion is the load-bearing part. `//evil.com` is PROTOCOL-relative:
 * it inherits the page's scheme and navigates off-site, while looking root-relative
 * to a naive `startsWith('/')`. Two further spellings of the same bypass are
 * rejected with it, because a browser normalises them before parsing:
 * `/\evil.com` (a backslash folds to a slash) and `/\n/evil.com` (control
 * characters are stripped, leaving `//`).
 */
export function isDocsLinkHref(value: string): boolean {
  if (isHttpsUrl(value)) return true
  // Whitespace, control characters, and backslashes never belong in a path we
  // emit; rejecting them outright removes the whole normalisation-bypass class.
  // eslint-disable-next-line no-control-regex
  if (/[\s\\]|[\u0000-\u001f\u007f]/.test(value)) return false
  return value.startsWith('/') && !value.startsWith('//')
}

export const DOCS_LINK_HREF_MESSAGE = 'Link href must be an absolute https: URL or a root-relative path'

// ============================================
// Block allowlist
// ============================================

/**
 * BlockNote default blocks the documentation vocabulary keeps. Prose, headings,
 * nested ordered lists, quote, code block, image, video, and divider — every
 * plain-markdown construct the existing corpus uses (R7).
 */
export const DOCS_DEFAULT_BLOCK_TYPES = [
  'paragraph',
  'heading',
  'quote',
  'bulletListItem',
  'numberedListItem',
  'checkListItem',
  'codeBlock',
  'image',
  'video',
  'divider',
  'table'
] as const

/**
 * BlockNote default blocks deliberately kept OUT. The docs editor filters its
 * default specs with exactly this list, under a
 * `satisfies readonly (keyof typeof defaultBlockSpecs)[]`, so a BlockNote
 * rename is a compile error there rather than a block that quietly comes back.
 *
 * `table` USED TO SIT HERE, and the ordering it was waiting on is the rule this
 * list exists to enforce: a block type may not join the allowlist until its
 * content shape and its sanitizer tags land in the same change (U3 did all
 * three). `audio` / `file` / `toggleListItem` stay out — no corpus page uses
 * them, and a toggle hides content from the page's table of contents the way
 * Rule P7 refuses tabs for.
 */
export const DOCS_EXCLUDED_DEFAULT_BLOCK_TYPES = ['audio', 'file', 'toggleListItem'] as const

/**
 * Blocks this product defines. `callout`, `stepList`, and `faq` are the
 * multi-part constructs; `step` and `faqItem` are the items a step list and a
 * FAQ are made of (KD10 — containers with children, never structured props).
 */
export const DOCS_CUSTOM_BLOCK_TYPES = ['callout', 'stepList', 'step', 'faq', 'faqItem'] as const

/**
 * `@blocknote/xl-multi-column`'s two block types. Not custom blocks: the editor
 * gets them from `withMultiColumn`, not from its own spec map. Nesting rules:
 * see the header.
 */
export const DOCS_COLUMN_BLOCK_TYPES = ['columnList', 'column'] as const

/** The closed set of block types a documentation page may contain. */
export const DOCS_BLOCK_TYPES = [
  ...DOCS_DEFAULT_BLOCK_TYPES,
  ...DOCS_CUSTOM_BLOCK_TYPES,
  ...DOCS_COLUMN_BLOCK_TYPES
] as const
export const DocsBlockTypeSchema = z.enum(DOCS_BLOCK_TYPES)
export type DocsBlockType = z.infer<typeof DocsBlockTypeSchema>

/**
 * The leaf blocks a prose container admits. One closed set, shared by callout,
 * step, and FAQ item, because the corpus gives no reason to differentiate them:
 * a warning callout in `troubleshooting/getting-help.mdx` holds a bulleted list,
 * steps in `start.mdx` hold prose plus a list plus a blockquote, and a FAQ answer
 * in `troubleshooting/common-issues.mdx` holds both list kinds.
 *
 * What it deliberately EXCLUDES, and why — this is a declared set, not "anything
 * that is not a container":
 *
 *   - `heading` — a heading inside a container is invisible to the page's table
 *     of contents, which is the same failure Rule P7 refuses tabs for.
 *   - `video`, `divider`, `table` — column-width blocks whose renderers assume
 *     they own the full measure; a divider's job is separating root sections,
 *     and a table narrowed to a callout's inset measure is unreadable at three
 *     columns, let alone four. No corpus page puts a table inside a container,
 *     so this excludes nothing anyone writes today (U3, deliberate).
 *   - the containers themselves.
 */
export const DOCS_CONTAINER_LEAF_TYPES = [
  'paragraph',
  'quote',
  'bulletListItem',
  'numberedListItem',
  'checkListItem',
  'codeBlock',
  'image'
] as const satisfies readonly DocsBlockType[]

/**
 * The multi-part constructs, and the column grid. These may appear ONLY at the
 * root of a page: a container never nests inside another container, which is
 * the invariant R8 and KD10 exist to protect — no documentation construct
 * acquires arbitrary block-in-block structure the way an MDX corpus does.
 */
export const DOCS_ROOT_ONLY_BLOCK_TYPES = [
  'callout',
  'stepList',
  'faq',
  'columnList'
] as const satisfies readonly DocsBlockType[]

/** Blocks that only ever appear inside their own container; never at the root. */
export const DOCS_CHILD_ONLY_BLOCK_TYPES = ['step', 'faqItem', 'column'] as const satisfies readonly DocsBlockType[]

/**
 * List items nest into one another — the one recursion that predates container
 * blocks (KD10 / Rule P2) — and may also hold a paragraph of continuation prose.
 * Still bounded: a nested list item may not have children of its own.
 */
export const DOCS_LIST_BLOCK_TYPES = [
  'bulletListItem',
  'numberedListItem',
  'checkListItem'
] as const satisfies readonly DocsBlockType[]

const DOCS_LIST_CHILD_TYPES = [...DOCS_LIST_BLOCK_TYPES, 'paragraph'] as const satisfies readonly DocsBlockType[]

/**
 * R8, mechanised: for EVERY block type, the closed set of child types it admits.
 * An empty set is a leaf.
 *
 * The `satisfies Record<DocsBlockType, …>` is the load-bearing part — adding a
 * block type to the allowlist without declaring what it may contain is a compile
 * error, so a new block cannot quietly inherit "anything". `table` declares `[]`
 * for exactly that reason: a table holds CONTENT (its own rows-and-cells shape),
 * never child blocks, so the columns cannot become another nesting axis.
 *
 * The only container-to-container edges are `stepList → step`, `faq → faqItem`
 * and `columnList → column`: a step list IS its steps, so the item is part of
 * the construct rather than something nested inside it. Every other edge lands
 * on a leaf.
 */
export const DOCS_BLOCK_CHILD_TYPES = {
  callout: DOCS_CONTAINER_LEAF_TYPES,
  stepList: ['step'],
  step: DOCS_CONTAINER_LEAF_TYPES,
  faq: ['faqItem'],
  faqItem: DOCS_CONTAINER_LEAF_TYPES,
  columnList: ['column'],
  column: DOCS_CONTAINER_LEAF_TYPES,
  bulletListItem: DOCS_LIST_CHILD_TYPES,
  numberedListItem: DOCS_LIST_CHILD_TYPES,
  checkListItem: DOCS_LIST_CHILD_TYPES,
  paragraph: [],
  heading: [],
  quote: [],
  codeBlock: [],
  image: [],
  video: [],
  divider: [],
  table: []
} as const satisfies Record<DocsBlockType, readonly DocsBlockType[]>

/** Callout styles the reader-side renderer actually implements. */
export const DOCS_CALLOUT_VARIANTS = ['info', 'tip', 'warning'] as const
export const DocsCalloutVariantSchema = z.enum(DOCS_CALLOUT_VARIANTS)
export type DocsCalloutVariant = z.infer<typeof DocsCalloutVariantSchema>

const ROOT_ONLY_TYPES = new Set<string>(DOCS_ROOT_ONLY_BLOCK_TYPES)
const CHILD_ONLY_TYPES = new Set<string>(DOCS_CHILD_ONLY_BLOCK_TYPES)
const LIST_TYPES = new Set<string>(DOCS_LIST_BLOCK_TYPES)
const ADMITTED_CHILD_TYPES = new Map<string, ReadonlySet<string>>(
  Object.entries(DOCS_BLOCK_CHILD_TYPES).map(([type, children]) => [type, new Set<string>(children)])
)

/**
 * Prop keys whose value must be an https URL. Matched exactly, plus any key
 * ending in `url` / `Url` so a prop added later inherits the constraint by
 * naming convention rather than by someone remembering this list.
 */
const EXPLICIT_URL_PROP_KEYS = new Set(['url', 'href', 'src'])

function isUrlPropKey(key: string): boolean {
  return EXPLICIT_URL_PROP_KEYS.has(key) || /url$/i.test(key)
}

/**
 * The https rule over a flat prop bag, applied wherever one appears — block
 * props and TABLE CELL props both. One implementation rather than two: a cell
 * prop bag is the same author-supplied record reaching the same renderer, and a
 * second copy of a security check is the copy that goes stale.
 */
function addUrlPropIssues(props: Record<string, unknown>, ctx: z.RefinementCtx, basePath: readonly string[]): void {
  for (const [key, value] of Object.entries(props)) {
    if (!isUrlPropKey(key)) continue
    // An empty string is BlockNote's "no file chosen yet" state on a freshly
    // inserted image or video block; a draft save must not fail on it.
    if (value === '') continue
    if (typeof value !== 'string' || !isHttpsUrl(value)) {
      ctx.addIssue({
        code: 'custom',
        path: [...basePath, key],
        message: `Block prop "${key}" must be an absolute https: URL`
      })
    }
  }
}

/** The fewest columns a `columnList` holds — xl-multi-column's own `column column+`. */
const DOCS_MIN_COLUMNS = 2

/** R17's count and R18's width: the two column rules the child-type map cannot express. */
function addColumnIssues(block: DocsBlock, ctx: z.RefinementCtx): void {
  if (block.type === 'columnList' && (block.children ?? []).length < DOCS_MIN_COLUMNS) {
    ctx.addIssue({ code: 'custom', path: ['children'], message: 'A columnList holds at least two columns' })
  }

  const width = block.props?.width
  if (block.type === 'column' && width !== undefined && !isColumnWidth(width)) {
    ctx.addIssue({ code: 'custom', path: ['props', 'width'], message: COLUMN_WIDTH_MESSAGE })
  }
}

/** Props a block type must carry, checked as present-and-a-string. */
const REQUIRED_STRING_PROPS = {
  step: 'title',
  faqItem: 'question'
} as const satisfies Partial<Record<DocsBlockType, string>>

// ============================================
// Inline content
// ============================================

/** BlockNote style marks — `bold: true`, `textColor: 'red'`, and friends. */
export const DocsInlineStylesSchema = z.record(z.string(), z.union([z.string(), z.boolean()]))
export type DocsInlineStyles = z.infer<typeof DocsInlineStylesSchema>

export const DocsStyledTextSchema = z
  .object({
    type: z.literal('text'),
    text: z.string().max(50_000),
    styles: DocsInlineStylesSchema.optional()
  })
  .strict()
export type DocsStyledText = z.infer<typeof DocsStyledTextSchema>

/**
 * Inline link. `href` is https-only for the same reason an image `url` is: a
 * `javascript:` href in stored content is a stored-XSS payload the renderer
 * would faithfully reproduce.
 */
export const DocsLinkSchema = z
  .object({
    type: z.literal('link'),
    href: z.string().max(2000).refine(isDocsLinkHref, { message: DOCS_LINK_HREF_MESSAGE }),
    content: z.array(DocsStyledTextSchema)
  })
  .strict()
export type DocsLink = z.infer<typeof DocsLinkSchema>

export const DocsInlineContentSchema = z.discriminatedUnion('type', [DocsStyledTextSchema, DocsLinkSchema])
export type DocsInlineContent = z.infer<typeof DocsInlineContentSchema>

// ============================================
// Props
// ============================================

/**
 * Block props are FLAT primitives only. BlockNote's own prop bags are flat
 * (`textAlignment`, `level`, `language`, `url`, `caption`, …), so this costs
 * nothing and removes an entire class of unvalidated nested payload. Table cell
 * props answer to the same schema for the same reason.
 */
export const DocsBlockPropsSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
export type DocsBlockProps = z.infer<typeof DocsBlockPropsSchema>

// ============================================
// Table content — the second content arm (R7 / KTD7)
// ============================================

/**
 * ONE table cell, bounded to INLINE content (KTD7, user-directed).
 *
 * `content` is an inline-content array and nothing else, so a cell cannot hold a
 * block — no list in a cell, no callout in a cell, no table in a cell. That bound
 * is what keeps "side-by-side content is a table" (KD12) from becoming the
 * arbitrary block-in-block nesting R8 exists to prevent: the table is a second
 * nesting axis, and this is where it stops. It also matches the corpus exactly —
 * every cell across the 13 raw-HTML tables and 4 markdown tables is text, bold,
 * inline code, or a link, and links are why cells admit `DocsLinkSchema` rather
 * than plain styled text (the `CardGroup` / `LinkCard` constructs flatten to
 * link-bearing tables on migration, per KD12).
 *
 * Cell props get the same https treatment as block props: BlockNote's own cell
 * props (`backgroundColor`, `textColor`, `textAlignment`, `colspan`, `rowspan`)
 * are not URL-bearing, so the check is inert TODAY and is here so that a future
 * BlockNote cell prop ending in `Url` cannot reach the renderer unvalidated.
 */
export const DocsTableCellSchema = z
  .object({
    type: z.literal('tableCell'),
    props: DocsBlockPropsSchema.optional(),
    content: z.array(DocsInlineContentSchema)
  })
  .strict()
  .superRefine((cell, ctx) => addUrlPropIssues(cell.props ?? {}, ctx, ['props']))
export type DocsTableCell = z.infer<typeof DocsTableCellSchema>

/** Bounds on a pasted table. Well past the corpus's widest (3) and longest. */
export const DOCS_MAX_TABLE_ROWS = 200
export const DOCS_MAX_TABLE_COLUMNS = 20

export const DocsTableRowSchema = z.object({ cells: z.array(DocsTableCellSchema).max(DOCS_MAX_TABLE_COLUMNS) }).strict()
export type DocsTableRow = z.infer<typeof DocsTableRowSchema>

/**
 * A table block's content — an OBJECT, not an array, which is the whole reason
 * this is a schema change rather than an allowlist entry (KTD7). `table` in
 * `DOCS_BLOCK_TYPES` without this arm would reject every table at the block
 * shape, before the type allowlist was ever consulted.
 *
 * Pinned to what BlockNote 0.52.1 actually emits (verified by round-tripping a
 * four-column table through `blocksToFullHTML` and back), not to what its types
 * permit:
 *
 *   - `columnWidths` admits `null` as well as a number, because the entries are
 *     `undefined` in memory and JSON turns an `undefined` ARRAY ELEMENT into
 *     `null` — the client sends nulls, so the boundary must accept them or every
 *     table fails on its first save.
 *   - `headerRows` / `headerCols` are omitted entirely when unset (JSON drops an
 *     undefined OBJECT VALUE), hence optional rather than nullable.
 *   - `rows[].cells` takes ONLY the `tableCell` object form. BlockNote's type
 *     also allows a bare `InlineContent[][]`, but the editor never produces it,
 *     and the migration (U12) must produce what the editor produces or the first
 *     edit of a migrated page rewrites the whole block.
 */
export const DocsTableContentSchema = z
  .object({
    type: z.literal('tableContent'),
    columnWidths: z.array(z.number().nullish()).max(DOCS_MAX_TABLE_COLUMNS).optional(),
    headerRows: z.number().int().nonnegative().optional(),
    headerCols: z.number().int().nonnegative().optional(),
    rows: z.array(DocsTableRowSchema).max(DOCS_MAX_TABLE_ROWS)
  })
  .strict()
export type DocsTableContent = z.infer<typeof DocsTableContentSchema>

// ============================================
// Block content
// ============================================

/**
 * A block's content: an array of inline content, or a table's rows-and-cells
 * object. The two arms are not interchangeable — `DocsBlockSchema` pins each
 * block type to exactly one of them, so a paragraph carrying table content (or a
 * table carrying an inline array) is rejected rather than handed to a renderer
 * that would silently drop it.
 */
export const DocsBlockContentSchema = z.union([z.array(DocsInlineContentSchema), DocsTableContentSchema])
export type DocsBlockContent = z.infer<typeof DocsBlockContentSchema>

/** The one block type whose content is the table arm. */
const TABLE_CONTENT_BLOCK_TYPE = 'table' satisfies DocsBlockType

// ============================================
// Block
// ============================================

export interface DocsBlock {
  id: string
  type: DocsBlockType
  props?: DocsBlockProps
  content?: DocsBlockContent
  children?: DocsBlock[]
}

/**
 * A single block. Recursive via `z.lazy` on `children`, with the one-level
 * bound enforced in the refinement rather than in the type — the type stays
 * recursive so an editor document assigns to it without reshaping.
 *
 * The cast pins the public type to the hand-written `DocsBlock` so declaration
 * emit stays concrete — a plain `z.infer` over the cycle widens to `unknown`.
 */
export const DocsBlockSchema: z.ZodType<DocsBlock> = z.lazy(() =>
  z
    .object({
      id: z.string().min(1).max(128),
      type: DocsBlockTypeSchema,
      props: DocsBlockPropsSchema.optional(),
      content: DocsBlockContentSchema.optional(),
      children: z.array(DocsBlockSchema).optional()
    })
    .strict()
    .superRefine((block, ctx) => {
      const props = block.props ?? {}
      const children = block.children ?? []

      addUrlPropIssues(props, ctx, ['props'])
      addColumnIssues(block, ctx)

      // THE CONTENT ARM IS PINNED TO THE BLOCK TYPE. The union above would
      // otherwise let a paragraph carry table content — which the renderer drops
      // on the floor — or a table carry an inline array, which is the very shape
      // KTD7 says a table is not. Only `table` takes the object arm, and it
      // takes nothing else.
      if (block.content !== undefined) {
        const isTableContent = !Array.isArray(block.content)
        const wantsTableContent = block.type === TABLE_CONTENT_BLOCK_TYPE
        if (isTableContent !== wantsTableContent) {
          ctx.addIssue({
            code: 'custom',
            path: ['content'],
            message: wantsTableContent
              ? 'A table block’s content must be a tableContent object, not an inline-content array'
              : `A ${block.type} block’s content must be an inline-content array, not a tableContent object`
          })
        }
      }

      if (block.type === 'callout' && !DocsCalloutVariantSchema.safeParse(props.variant).success) {
        ctx.addIssue({
          code: 'custom',
          path: ['props', 'variant'],
          message: `A callout block must carry a variant prop, one of: ${DOCS_CALLOUT_VARIANTS.join(', ')}`
        })
      }

      const requiredProp = REQUIRED_STRING_PROPS[block.type as keyof typeof REQUIRED_STRING_PROPS]
      // Present-but-empty is tolerated: a freshly inserted block carries its
      // prop defaults, and a draft save must not fail before the author types.
      if (requiredProp && typeof props[requiredProp] !== 'string') {
        ctx.addIssue({
          code: 'custom',
          path: ['props', requiredProp],
          message: `A ${block.type} block must carry a ${requiredProp} prop`
        })
      }

      // An EMPTY container is allowed — that is what a freshly inserted one
      // looks like before the author has added anything to it.
      const admitted = ADMITTED_CHILD_TYPES.get(block.type)

      if (admitted && admitted.size === 0 && children.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['children'],
          message: `A ${block.type} block takes no children`
        })
      }

      children.forEach((child, index) => {
        if (admitted && admitted.size > 0 && !admitted.has(child.type)) {
          ctx.addIssue({
            code: 'custom',
            path: ['children', index, 'type'],
            message: `A ${block.type} block admits only ${[...admitted].join(', ')} children, not ${child.type}`
          })
        }

        // REDUNDANT against the allowlist above as the sets stand today — no
        // admitted set contains a root-only container, so nothing reaches this.
        // It is kept as the backstop for the one edit that would break R8:
        // adding a container to `DOCS_CONTAINER_LEAF_TYPES`. The constant-level
        // test ("never lets a container admit a container") fails on that edit,
        // and this keeps the runtime behaviour correct meanwhile.
        if (ROOT_ONLY_TYPES.has(child.type)) {
          ctx.addIssue({
            code: 'custom',
            path: ['children', index, 'type'],
            message: `A ${child.type} container may not nest inside another block; it belongs at the root of the page`
          })
        }

        // The list recursion is the one cycle in the child-type graph, so it is
        // the one place depth needs its own bound: a list item that is itself a
        // child carries no children. Everything else is bounded structurally —
        // containers admit leaves, and leaves admit nothing.
        if (LIST_TYPES.has(child.type) && (child.children ?? []).length > 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['children', index, 'children'],
            message: 'List nesting is bounded to one level: a nested list item may not have children of its own'
          })
        }
      })
    })
) as z.ZodType<DocsBlock>

/** Guard against a runaway paste; well past any real documentation page. */
export const DOCS_MAX_BLOCKS = 2000

/**
 * A documentation page body: the editor document, a flat array of root blocks.
 * Parsing is non-destructive — no defaults, no transforms, no stripping — so a
 * valid document round-trips byte-comparable with what the editor produced.
 */
export const DocsContentSchema = z
  .array(DocsBlockSchema)
  .max(DOCS_MAX_BLOCKS)
  .superRefine((blocks, ctx) => {
    blocks.forEach((block, index) => {
      if (CHILD_ONLY_TYPES.has(block.type)) {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'type'],
          message: `A ${block.type} block only exists inside its container, never at the root of a page`
        })
      }
    })
  })
export type DocsContent = z.infer<typeof DocsContentSchema>
